import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import * as THREE from 'three';
import { fetchModelAsset } from '../../services/modelService';
import { createHtmlModelBlob, applyHtmlModelSettings } from './htmlModelController';

const normalize = (value) => String(value || '').replace(/:\d+$/, '').toLowerCase().replace(/[^a-z0-9]/g, '');
const isMesh = (object) => object && (object.isMesh || object.isSkinnedMesh);
const isMeaningfulNodeName = (value) => {
    const name = String(value || '').trim().replace(/_instance_\d+$/i, '');
    return !!name && !/^(?:mesh|node|primitive)_\d+(?:_\d+)*(?:_instance_\d+)?$/i.test(name);
};

const HtmlModelScene = forwardRef(function HtmlModelScene({
    modelUrl, modelVisible = true, showGizmo = true,
    onPartSelected, onPartDoubleClick, onAssemblyMapped, onSelectionChange,
    onVisibilityChange, resolveSelectionNode, explodeFactor = 0, onModelLoaded,
    onError, onContextMenu, onPointerMissed, onControllerReady, onLoadingProgress, navMode, highlightColor,
    highlightOpacity = 0.45, modelAxis, modelOrientation, defaultCameraCorner, lightSettings,
}, ref) {
    const iframeRef = useRef(null);
    const blobUrlRef = useRef('');
    const viewerRef = useRef(null);
    const partsRef = useRef([]);
    const assemblyNodesRef = useRef(new Map());
    const parentNodesRef = useRef(new Map());
    const nodesByProductRef = useRef(new Map());
    const selectionRef = useRef([]);
    const originalsRef = useRef(new Map());
    const hiddenRef = useRef([]);
    const callbacksRef = useRef({});
    callbacksRef.current = { onPartSelected, onPartDoubleClick, onAssemblyMapped, onSelectionChange, onVisibilityChange, resolveSelectionNode, onModelLoaded, onError, onContextMenu, onPointerMissed, onControllerReady, onLoadingProgress };

    useEffect(() => {
        let cancelled = false;
        const controller = new AbortController();
        const load = async () => {
            if (!modelUrl) return;
            try {
                const response = await fetchModelAsset(modelUrl, { signal: controller.signal });
                if (!response.ok) throw new Error(`Unable to load HTML model (${response.status}).`);
                const blob = await createHtmlModelBlob(response, modelUrl, (percent) => callbacksRef.current.onLoadingProgress?.(percent));
                const nextUrl = URL.createObjectURL(blob);
                if (cancelled) { URL.revokeObjectURL(nextUrl); return; }
                if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
                blobUrlRef.current = nextUrl;
                if (iframeRef.current) iframeRef.current.src = nextUrl;
            } catch (error) {
                if (!cancelled && error.name !== 'AbortError') callbacksRef.current.onError?.(error.message || 'Failed to load the HTML model.');
            }
        };
        load();
        return () => { cancelled = true; controller.abort(); if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current); };
    }, [modelUrl]);

    useEffect(() => {
        const frame = iframeRef.current;
        if (!frame) return undefined;
        let poll;
        let observedSelection = '';
        let initializedViewer = null;
        let disposed = false;
        const getViewer = () => {
            try { return frame.contentWindow?.iSTP2HTML || null; } catch (_) { return null; }
        };
        const mapParts = (viewer) => {
            const nativeParts = Array.isArray(viewer?.state?.parts) ? viewer.state.parts : [];
            partsRef.current = [];
            const root = viewer?.state?.root;
            assemblyNodesRef.current = new Map();
            parentNodesRef.current = new Map();
            nodesByProductRef.current = new Map();
            // Build the portal tree from selectable converter parts and their
            // ancestor chains. Walking every Object3D/mesh in the model here
            // blocked Chrome's main thread after the native loader completed.
            const rootName = root?.name || 'Model';
            const resolved = { name: rootName, productRef: rootName, objects: [], children: [] };
            const nodeByObject = new Map();
            if (root) nodeByObject.set(root, resolved);
            const registerNode = (node, parent) => {
                if (parent) parent.children.push(node);
                parentNodesRef.current.set(node, parent);
                const key = normalize(node.productRef);
                const matches = nodesByProductRef.current.get(key) || [];
                matches.push(node);
                nodesByProductRef.current.set(key, matches);
            };
            return new Promise((resolve) => {
                let index = 0;
                const mapBatch = () => {
                    if (disposed) { resolve(null); return; }
                    const end = Math.min(index + 250, nativeParts.length);
                    for (; index < end; index += 1) {
                        const nativePart = nativeParts[index];
                        if (!nativePart || !(nativePart.meshes?.some(isMesh) || isMesh(nativePart.object))) continue;
                        partsRef.current.push(nativePart);
                        const owner = nativePart.object || nativePart.meshes?.find(Boolean);
                        if (!owner) continue;
                        const ancestors = [];
                        let current = owner;
                        while (current && current !== root) {
                            ancestors.push(current);
                            current = current.parent;
                        }
                        let parentNode = resolved;
                        for (let ancestorIndex = ancestors.length - 1; ancestorIndex >= 0; ancestorIndex -= 1) {
                            const object = ancestors[ancestorIndex];
                            let node = nodeByObject.get(object);
                            if (!node) {
                                const isPartOwner = object === owner;
                                const objectName = String(object.name || '').replace(/_instance_\d+$/i, '').trim();
                                if (!isPartOwner && !isMeaningfulNodeName(object.name)) continue;
                                const name = isPartOwner ? (nativePart.name || objectName || 'Part') : objectName;
                                node = { name, productRef: name, objects: [object], children: [] };
                                nodeByObject.set(object, node);
                                registerNode(node, parentNode);
                            }
                            if (object === owner) {
                                node.nativePart = nativePart;
                                if (!node.objects.includes(owner)) node.objects.push(owner);
                                assemblyNodesRef.current.set(nativePart, node);
                            }
                            parentNode = node;
                        }
                        if (owner === root) {
                            resolved.nativePart = nativePart;
                            if (!resolved.objects.includes(owner)) resolved.objects.push(owner);
                            assemblyNodesRef.current.set(nativePart, resolved);
                        }
                    }
                    if (index < nativeParts.length) {
                        // Yield between batches so large converter part lists do not
                        // monopolize Chrome's main thread and trigger Page Unresponsive.
                        setTimeout(mapBatch, 0);
                        return;
                    }
                    callbacksRef.current.onAssemblyMapped?.(resolved);
                    resolve(resolved);
                };
                mapBatch();
            });
        };
        const onNativeReady = (event) => {
            if (event.source !== frame.contentWindow || event.data?.source !== 'inhouse') return;
            if (event.data.stage === 'progress') {
                const percent = Number(event.data.percent);
                if (Number.isFinite(percent)) callbacksRef.current.onLoadingProgress?.(20 + (Math.min(99, Math.max(0, percent)) * 0.8));
                return;
            }
            // file-loaded is handled by the iframe readiness poll below so the
            // portal initializes its selection observer through one code path.
        };
        window.addEventListener('message', onNativeReady);
        const onLoad = () => {
            const win = frame.contentWindow;
            if (!win) return;
            const start = Date.now();
            if (poll) win.clearInterval(poll);
            poll = win.setInterval(() => {
                const viewer = getViewer();
                const loader = win.document.getElementById('loader');
                const nativeLoadComplete = loader?.classList.contains('done') === true;
                // Parts can legitimately be empty in a valid model (or in older
                // converter output). The converter's loader is its authoritative
                // completion signal; requiring state.parts here left the portal
                // loader running forever after the model itself had finished.
                const hasNativeParts = viewer?.state?.parts?.length > 0;
                const loadComplete = loader ? nativeLoadComplete : hasNativeParts;
                if (!viewer || !loadComplete) {
                    if (Date.now() - start > 60000) { win.clearInterval(poll); callbacksRef.current.onError?.('The HTML viewer did not finish loading the model.'); }
                    return;
                }
                if (initializedViewer === viewer) { win.clearInterval(poll); return; }
                initializedViewer = viewer;
                win.clearInterval(poll);
                viewerRef.current = viewer;
                callbacksRef.current.onControllerReady?.(viewer);
                // The converter has finished loading and can render the model now.
                // Master drops its loading curtain at this point; portal assembly
                // indexing is extra work and must not keep the model hidden.
                callbacksRef.current.onModelLoaded?.();
                win.setTimeout(() => {
                    if (disposed) return;
                    applyHtmlModelSettings(viewer, win, { navMode, explodeFactor, lightSettings, highlightColor, highlightOpacity, modelAxis, modelOrientation, defaultCameraCorner });
                    mapParts(viewer);
                }, 0);
                const publishSelection = (part) => {
                    const key = part ? String(part.id ?? part.name ?? '') : '';
                    if (key === observedSelection) return;
                    observedSelection = key;
                    selectionRef.current = part ? [part] : [];
                    if (part) {
                        const mesh = part.meshes?.[0] || part.object;
                        const rawNode = assemblyNodesRef.current.get(part) || { name: part.name, productRef: part.name, objects: [part.object].filter(Boolean), children: [] };
                        const resolved = callbacksRef.current.resolveSelectionNode?.(rawNode);
                        const assemblyNode = resolved?.targetNode || rawNode;
                        const material = mesh?.material;
                        const materialInfo = material?.color ? { name: material.name, color: `#${material.color.getHexString()}` } : material ? { name: material.name || '', color: null } : null;
                        const meta = { name: mesh?.name || part.name, type: mesh?.type || 'Mesh', object3D: mesh, uuid: mesh?.uuid, userData: mesh?.userData, material: materialInfo, assemblyNode, selectionPoint: null };
                        callbacksRef.current.onPartSelected?.(meta);
                        callbacksRef.current.onSelectionChange?.([meta]);
                    } else {
                        callbacksRef.current.onSelectionChange?.([]);
                        callbacksRef.current.onPointerMissed?.();
                    }
                };
                if (typeof viewer.selectPart === 'function' && !viewer.__iecomSelectionBridge) {
                    const selectPart = viewer.selectPart;
                    viewer.selectPart = function (part, options) {
                        const result = selectPart.call(viewer, part, options);
                        publishSelection(part || null);
                        return result;
                    };
                    viewer.__iecomSelectionBridge = true;
                }
                const canvas = win.document.getElementById('glcanvas');
                if (canvas) {
                    let pointerStart = null;
                    const raycaster = new THREE.Raycaster();
                    const pointer = new THREE.Vector2();
                    canvas.addEventListener('contextmenu', (event) => callbacksRef.current.onContextMenu?.(event));
                    canvas.addEventListener('pointerdown', (event) => {
                        if (event.button === 0) pointerStart = { x: event.clientX, y: event.clientY, time: Date.now(), selected: viewer.state.selected };
                    });
                    canvas.addEventListener('pointerup', (event) => {
                        const start = pointerStart;
                        pointerStart = null;
                        if (!start || event.button !== 0 || Date.now() - start.time > 400 || Math.hypot(event.clientX - start.x, event.clientY - start.y) > 4 || viewer.state.selected !== start.selected) return;
                        const rect = canvas.getBoundingClientRect();
                        if (!rect.width || !rect.height || !viewer.state.root || !viewer.camera) return;
                        pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
                        pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
                        raycaster.setFromCamera(pointer, viewer.camera);
                        const hit = raycaster.intersectObject(viewer.state.root, true)[0];
                        if (!hit) return;
                        let part = viewer.state.partByObject?.get(hit.object) || null;
                        for (let object = hit.object; !part && object; object = object.parent) {
                            part = viewer.state.partByObject?.get(object) || null;
                            if (object === viewer.state.root) break;
                        }
                        if (part) viewer.selectPart(part, { focus: false });
                    });
                    canvas.addEventListener('dblclick', (event) => {
                        const part = viewer.state.selected;
                        if (part) callbacksRef.current.onPartDoubleClick?.({ name: part.name, type: 'mesh', object3D: part.object, uuid: part.object?.uuid, userData: part.object?.userData, material: part.object?.material });
                    });
                }
                const tick = () => {
                    publishSelection(viewer.state.selected || null);
                };
                poll = win.setInterval(tick, 120);
            }, 100);
        };
        frame.addEventListener('load', onLoad);
        return () => { disposed = true; window.removeEventListener('message', onNativeReady); frame.removeEventListener('load', onLoad); try { if (poll) frame.contentWindow?.clearInterval(poll); } catch (_) {} viewerRef.current = null; callbacksRef.current.onControllerReady?.(null); };
    }, [modelUrl]);

    useEffect(() => {
        const frame = iframeRef.current;
        if (!frame) return;
        try {
            const doc = frame.contentDocument;
            const cube = doc?.getElementById('viewcube');
            if (cube) cube.style.display = 'none';
        } catch (_) {}
    }, [showGizmo, modelUrl]);

    useEffect(() => {
        if (viewerRef.current) applyHtmlModelSettings(viewerRef.current, iframeRef.current?.contentWindow, { navMode, explodeFactor, lightSettings, highlightColor, highlightOpacity, modelAxis, modelOrientation, defaultCameraCorner });
    }, [navMode, explodeFactor, lightSettings, highlightColor, highlightOpacity, modelAxis, modelOrientation, defaultCameraCorner]);

    const setVisible = (part, visible) => {
        if (part.object) part.object.visible = visible;
        (part.meshes || [part.object]).filter(Boolean).forEach((mesh) => { mesh.visible = visible; });
        part.visible = visible;
        callbacksRef.current.onVisibilityChange?.(Object.fromEntries(partsRef.current.map((item) => [item.name, item.visible !== false])));
    };
    const partsForRefs = (refs = []) => {
        const keys = new Set(refs.map(normalize));
        const matchedNodes = Array.from(keys).flatMap((key) => nodesByProductRef.current.get(key) || []);
        const underMatchedNode = (part) => matchedNodes.some((node) => {
            const root = node.objects?.[0];
            return root && (root === part.object || root.children?.some((child) => child === part.object) || part.object?.parent === root || (() => { let current = part.object; while (current) { if (current === root) return true; current = current.parent; } return false; })());
        });
        return partsRef.current.filter((part) => keys.has(normalize(part.name)) || underMatchedNode(part));
    };
    const partsForSelectionKeys = (keys = []) => keys.flatMap((key) => {
        const [kind, value] = String(key || '').split(/:(.*)/s, 2);
        if (kind === 'u') return partsRef.current.filter((part) => part.object?.uuid === value || part.meshes?.some((mesh) => mesh.uuid === value));
        return partsForRefs([kind === 'p' ? value : key]);
    });
    const setPartsTransparent = (parts, opacity = 0.2) => parts.forEach((part) => (part.meshes || [part.object]).filter(Boolean).forEach((mesh) => {
        const base = mesh.userData?._mat || mesh.material;
        if (!originalsRef.current.has(mesh)) originalsRef.current.set(mesh, { material: base, highlighted: !!mesh.userData?._mat });
        const saved = originalsRef.current.get(mesh);
        const transparentBase = Array.isArray(saved.material) ? saved.material.map((material) => material.clone()) : saved.material.clone();
        const materials = Array.isArray(transparentBase) ? transparentBase : [transparentBase];
        materials.forEach((material) => { material.transparent = true; material.opacity = opacity; material.depthWrite = false; material.needsUpdate = true; });
        if (saved.highlighted) mesh.userData._mat = Array.isArray(transparentBase) ? transparentBase[0] : transparentBase;
        else mesh.material = transparentBase;
        viewerRef.current?.requestRender?.();
    }));
    const restorePartsMaterial = (parts) => parts.forEach((part) => (part.meshes || [part.object]).filter(Boolean).forEach((mesh) => {
        if (originalsRef.current.has(mesh)) { const saved = originalsRef.current.get(mesh); if (saved.highlighted) mesh.userData._mat = saved.material; else mesh.material = saved.material; originalsRef.current.delete(mesh); }
        viewerRef.current?.requestRender?.();
    }));
    const selectParts = (parts, focus = false, emitSelectionCallbacks = true) => {
        const viewer = viewerRef.current;
        if (!viewer || !parts.length) return false;
        viewer.selectPart(parts[0], { focus, fromTree: true });
        selectionRef.current = [parts[0]];
        if (!emitSelectionCallbacks) silentSelectionRef.current = parts[0].id || parts[0].name;
        if (parts.length > 1) {
            applyOccurrenceHighlights(viewer, parts);
            viewer.state.selectVisibleActive = true;
            viewer.requestRender?.();
        }
        return true;
    };
    const clearOccurrenceHighlights = (viewer) => {
        for (const part of viewer?.state?.visibleSelection || []) {
            for (const mesh of part.meshes || []) {
                if (mesh.userData?._mat) { mesh.material = mesh.userData._mat; delete mesh.userData._mat; }
            }
        }
        if (viewer?.state) viewer.state.visibleSelection = [];
    };
    const applyOccurrenceHighlights = (viewer, parts) => {
        clearOccurrenceHighlights(viewer);
        const highlight = parts[0]?.meshes?.[0]?.material;
        for (const part of parts) {
            for (const mesh of part.meshes || []) {
                if (mesh.userData?._mat) continue;
                mesh.userData._mat = mesh.material;
                mesh.material = highlight;
            }
        }
        viewer.state.visibleSelection = parts;
    };
    const silentSelectionRef = useRef(null);
    useImperativeHandle(ref, () => ({
        resetView: () => viewerRef.current?.setView?.(defaultCameraCorner || 'isometric'),
        setIsometricView: () => viewerRef.current?.setView?.('isometric'),
        resetModel: () => { partsRef.current.forEach((part) => setVisible(part, true)); originalsRef.current.forEach((material, mesh) => { mesh.material = material; }); originalsRef.current.clear(); const viewer = viewerRef.current; if (viewer?.state) { viewer.state.visibleSelection = []; viewer.state.selectVisibleActive = false; } viewer?.requestRender?.(); },
        clearSelection: () => { const viewer = viewerRef.current; if (viewer?.state) { clearOccurrenceHighlights(viewer); viewer.selectPart(null); viewer.state.selectVisibleActive = false; viewer.requestRender?.(); } },
        canSelectAssemblyNode: (productRef) => partsForRefs([productRef]).length > 0,
        selectAssemblyNode: (productRef) => selectParts(partsForRefs([productRef]), false),
        selectAssemblySubtreeByProductRefs: (refs, options = {}) => {
            const requested = Array.isArray(refs) ? refs : [refs];
            const primary = partsForRefs([options.primaryProductRef || requested[0]]);
            const parts = options.includeAllOccurrences === false ? primary.slice(0, 1) : partsForRefs(requested);
            return selectParts(parts, options.focus !== false, options.emitSelectionCallbacks !== false);
        },
        selectAllOccurrences: () => { const selected = selectionRef.current[0]; if (!selected) return; const occurrences = partsForRefs([selected.name]); const viewer = viewerRef.current; viewer?.selectPart?.(selected, { focus: false, fromTree: true }); if (viewer?.state) { applyOccurrenceHighlights(viewer, occurrences); viewer.state.selectVisibleActive = true; viewer.requestRender?.(); } },
        selectSingleOccurrence: (uuid) => selectParts(partsRef.current.filter((part) => part.object?.uuid === uuid), false),
        setAssemblyNodeVisibility: (refValue, visible) => { partsForRefs([refValue]).forEach((part) => setVisible(part, visible)); viewerRef.current?.requestRender?.(); },
        setAssemblyNodesVisibility: (refs, visible) => { if (!Array.isArray(refs) || !refs.length) { callbacksRef.current.onVisibilityChange?.(Object.fromEntries(partsRef.current.map((item) => [item.name, item.visible !== false]))); return; } partsForRefs(refs).forEach((part) => setVisible(part, visible)); viewerRef.current?.requestRender?.(); },
        hideSelected: () => { selectionRef.current.forEach((part) => { hiddenRef.current.push(part); setVisible(part, false); }); viewerRef.current?.requestRender?.(); },
        unhideLastHidden: () => { const part = hiddenRef.current.pop(); if (!part) return false; setVisible(part, true); viewerRef.current?.requestRender?.(); return true; },
        isolateSelected: () => { const keep = new Set(selectionRef.current); partsRef.current.forEach((part) => { if (!keep.has(part)) setVisible(part, false); }); viewerRef.current?.requestRender?.(); },
        isolatePart: (part) => { const chosen = partsForRefs([part?.productRef || part?.name || part]); partsRef.current.forEach((item) => setVisible(item, chosen.includes(item))); viewerRef.current?.requestRender?.(); },
        hideUnhideParts: (parts) => { partsForRefs((parts || []).map((item) => item?.productRef || item?.name || item)).forEach((part) => setVisible(part, !part.visible)); viewerRef.current?.requestRender?.(); },
        showOnlyProductRefs: (refs, options = {}) => {
            const keep = new Set(partsForRefs(refs));
            const emptyMode = options.emptyMode || 'showAll';
            partsRef.current.forEach((part) => setVisible(part, !refs?.length ? emptyMode !== 'hideAll' : keep.has(part)));
            viewerRef.current?.requestRender?.();
        },
        makeSelectedTransparent: () => setPartsTransparent(selectionRef.current),
        makeSelectedSolid: () => restorePartsMaterial(selectionRef.current),
        setSelectionKeysTransparent: (keys, options = {}) => setPartsTransparent(partsForSelectionKeys(keys), options.opacity),
        setSelectionKeysSolid: (keys) => restorePartsMaterial(partsForSelectionKeys(keys)),
        showMachineTransparentExceptSelected: (options = {}) => { const keep = new Set(selectionRef.current); setPartsTransparent(partsRef.current.filter((part) => !keep.has(part)), options.opacity || 0.15); },
        showMachineTransparentExceptSelectionKeys: (keys, options = {}) => { const keep = new Set(partsForSelectionKeys(keys)); setPartsTransparent(partsRef.current.filter((part) => !keep.has(part)), options.opacity || 0.15); },
        clearGlobalTransparency: () => { originalsRef.current.forEach((material, mesh) => { mesh.material = material; }); originalsRef.current.clear(); viewerRef.current?.requestRender?.(); },
        addToHighlight: () => {}, removeHighlight: () => {},
        zoomInStep: () => { const viewer = viewerRef.current; if (viewer?.camera && viewer.controls?.target) { viewer.camera.position.lerp(viewer.controls.target, 0.12); viewer.controls.update?.(); viewer.requestRender?.(); } },
        zoomOutStep: () => { const viewer = viewerRef.current; if (viewer?.camera && viewer.controls?.target) { viewer.camera.position.lerp(viewer.controls.target, -0.12); viewer.controls.update?.(); viewer.requestRender?.(); } },
        zoomToSelectionToggle: () => { const viewer = viewerRef.current; const part = selectionRef.current[0]; if (part?.object) viewer?.fitToPart?.(new THREE.Box3().setFromObject(part.object)); else viewer?.setView?.('isometric'); },
        selectVisibleParts: () => {
            const visible = partsRef.current.filter((part) => part.visible !== false);
            if (visible.length) selectParts(visible, false);
            return visible.map((part) => {
            const assemblyNode = { name: part.name, productRef: part.name, objects: [part.object].filter(Boolean), children: [] };
            return { name: part.name, uuid: part.object?.uuid, object3D: part.object, assemblyNode };
            });
        },
        stepShowParentForSelection: () => {
            const selected = selectionRef.current[0];
            const node = assemblyNodesRef.current.get(selected);
            const parent = node && parentNodesRef.current.get(node);
            if (!parent) return false;
            const children = partsForRefs([parent.productRef]);
            return selectParts(children, false);
        },
    }), [defaultCameraCorner]);

    return <iframe ref={iframeRef} title="In-house 3D model" src="about:blank" aria-hidden={!modelVisible} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0, display: modelVisible ? 'block' : 'none', background: 'transparent' }} />;
});

export default HtmlModelScene;
